import { combineReducers } from 'redux';
import { urlsWrapper } from 'config';
import { serialize } from 'utils';
import { commonGet, commonPost } from 'modules/api';
import {
  saveData,
  createCommonItemConstants,
  createCommonItemReducer,
  createCommonListConstants,
  createCommonListReducer,
} from './utils';

const scoutsConstants = createCommonItemConstants('ANALYTICS_SCOUTS');
const cropsConstants = createCommonItemConstants('ANALYTICS_CROPS');
const cropsByIdConstants = createCommonItemConstants('ANALYTICS_CROPS_BY_ID');
const cropsScoutProblemsConstants = createCommonItemConstants(
  'ANALYTICS_CROPS_SCOUTPROBLEMS'
);
const usedMaterialConstants = createCommonItemConstants(
  'ANALYTICS_USED_MATERIAL_BY_CROPS_ID'
);
const workQualityConstants = createCommonListConstants(
  'ANALYTICS_WORK_QUALITY'
);

// reducer
const scouts = createCommonItemReducer(scoutsConstants);
const crops = createCommonItemReducer(cropsConstants);
const cropsById = createCommonItemReducer(cropsByIdConstants);
const cropsScoutProblems = createCommonItemReducer(cropsScoutProblemsConstants);
const usedMaterial = createCommonItemReducer(usedMaterialConstants);
const workQuality = createCommonListReducer(workQualityConstants);

export default combineReducers({
  scouts,
  crops,
  cropsById,
  cropsScoutProblems,
  usedMaterial,
  workQuality,
});

// actions
export function getAnalyticsScouts(params) {
  return saveData(
    `${urlsWrapper('analyticsScouts')}?${serialize(params)}`,
    scoutsConstants.SET_ITEM,
    scoutsConstants.SET_IS_PENDING
  );
}

export function getAnalyticsCrops(params) {
  return saveData(
    `${urlsWrapper('analyticsCrops')}?${serialize(params)}`,
    cropsConstants.SET_ITEM,
    cropsConstants.SET_IS_PENDING
  );
}

export function getNdviDynamics(params) {
  return commonGet({
    url: `${urlsWrapper('ndviDynamics')}?${serialize(params)}`,
  });
}

const SCENES_DOWNLOADS_POLL_INTERVAL = 5000;
// Не ждём загрузку снимков бесконечно: если за это время не скачалось,
// показываем аналитику по тому, что есть
const SCENES_DOWNLOADS_MAX_WAIT = 10 * 60 * 1000;
// Больше полей за раз не догружаем: историю по ним загружает поддержка
export const SCENES_DOWNLOADS_MAX_FIELDS = 100;
export const SCENES_GROUP_DOWNLOADS_ENABLED = true;

const getScenesDownloads = (params) =>
  commonGet({
    url: `${urlsWrapper('scenesDownloads')}?${serialize(params)}`,
  }).then((r) => r?.data?.summary || {});

const requestScenesDownloads = (params) =>
  commonPost({
    url: `${urlsWrapper('scenesDownloads')}?${serialize({
      ...params,
      mode: 'fast',
    })}`,
  });

const wait = (ms, signal) => {
  if (signal?.aborted) return Promise.resolve();
  return new Promise((resolve) => {
    const finish = () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', finish);
      resolve();
    };
    const timer = setTimeout(finish, ms);
    signal?.addEventListener('abort', finish, { once: true });
  });
};

// Перед запросом аналитики NDVI по полям догружаем недостающие снимки Sentinel
// за период (satreceiver#291): незагруженные ставим в очередь и ждём, пока
// скачаются все, что уже в очереди. onProgress получает summary.download_progress,
// isCancelled позволяет прервать ожидание (новый запрос, уход со страницы)
export async function downloadFieldScenesIfNeeded({
  fieldIds,
  startDate,
  endDate,
  onProgress = () => {},
  isCancelled = () => false,
  signal,
}) {
  const cancelled = () => signal?.aborted || isCancelled();
  if (cancelled() || !fieldIds?.length) return;
  const params = {
    start_date: startDate,
    end_date: endDate,
    field_ids: fieldIds.join(','),
    source_type: 'SENTINEL2',
    max_cloud_coverage: 0.01,
  };
  const startedAt = Date.now();

  let summary = await getScenesDownloads(params);
  if (summary.preprocessed_scenes > 0 && !cancelled()) {
    await requestScenesDownloads(params);
    await wait(SCENES_DOWNLOADS_POLL_INTERVAL, signal);
    if (cancelled()) return;
    summary = await getScenesDownloads(params);
  }

  while (
    !cancelled() &&
    (summary.requested_scenes > 0 || summary.processing_scenes > 0) &&
    Date.now() - startedAt < SCENES_DOWNLOADS_MAX_WAIT
  ) {
    onProgress(summary.download_progress || 0);
    // eslint-disable-next-line no-await-in-loop
    await wait(SCENES_DOWNLOADS_POLL_INTERVAL, signal);
    if (cancelled()) return;
    // eslint-disable-next-line no-await-in-loop
    summary = await getScenesDownloads(params);
  }
}

export function getCropsWithoutSave(params) {
  return commonGet({
    url: `${urlsWrapper('analyticsCrops')}?${serialize(params)}`,
  });
}

export function getAnalyticsCropsById(cropId, params) {
  return saveData(
    `${urlsWrapper('analyticsCrops')}/${cropId}?${serialize(params)}`,
    cropsByIdConstants.SET_ITEM,
    cropsByIdConstants.SET_IS_PENDING
  );
}

export const getAnalyticsCropsScoutProblems = ({ crop_id, ...params }) =>
  saveData(
    `${urlsWrapper('analyticsCrops')}/${crop_id}/scoutproblems?${serialize(
      params
    )}`,
    cropsScoutProblemsConstants.SET_ITEM,
    cropsScoutProblemsConstants.SET_IS_PENDING
  );

export function getAnalyticsUsedMaterialByCrop(cropId, params) {
  return saveData(
    `${urlsWrapper('analyticsCrops')}/${cropId}/inputs?${serialize(params)}`,
    usedMaterialConstants.SET_ITEM,
    usedMaterialConstants.SET_IS_PENDING
  );
}

export function getAnalyticsWorkQuality(cropId, params) {
  return saveData(
    `${urlsWrapper('analyticsCrops')}/${cropId}/workquality?${serialize(
      params
    )}`,
    workQualityConstants.SET_LIST,
    workQualityConstants.SET_IS_PENDING
  );
}

export const getFilledTstrials = (params) =>
  commonGet({
    url: `${urlsWrapper('filledTstrials')}`,
    params,
  });

export const getFilledResultsTstrials = (params) =>
  commonGet({
    url: `${urlsWrapper('filledTstrialsResults')}`,
    params,
  });

export const getFilledProductsTstrials = (params) =>
  commonGet({
    url: `${urlsWrapper('filledTstrialsProducts')}`,
    params,
  });

export const getFilledDivisionTstrials = ({ selectedDivision, ...params }) =>
  commonGet({
    url: `${urlsWrapper('filledTstrialsFields')}`,
    params: {
      ...params,
      division_ids: selectedDivision,
    },
  });

export const getTwoBreedsGraphData = (params) =>
  commonGet({
    url: `${urlsWrapper('twoBreedsCompare')}`,
    params,
  });

export const getCompetitorsGraphData = (params) =>
  commonGet({
    url: `${urlsWrapper('competitorsCompare')}`,
    params,
  });
